import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSession } from './SessionProvider.jsx';
import { synchronizeViewerProgress } from '../../services/playback/cloudProgressService.js';

const ProgressSyncContext = createContext({ ready: true, syncing: false, error: null, refresh: async () => null });

export function ProgressSyncProvider({ children }) {
  const { ready: sessionReady, user } = useSession();
  const [state, setState] = useState({ ready: !user, syncing: false, error: null });

  const refresh = useCallback(async () => {
    if (!sessionReady || !user?.id) {
      setState({ ready: true, syncing: false, error: null });
      return null;
    }
    setState((current) => ({ ...current, syncing: true, error: null }));
    try {
      const result = await synchronizeViewerProgress(user);
      setState({ ready: true, syncing: false, error: null });
      return result;
    } catch (error) {
      // Local resume remains usable even if cloud synchronization is unavailable.
      setState({ ready: true, syncing: false, error });
      return null;
    }
  }, [sessionReady, user?.id]);

  useEffect(() => {
    let active = true;
    if (!sessionReady) return undefined;
    if (!user?.id) {
      setState({ ready: true, syncing: false, error: null });
      return undefined;
    }
    setState({ ready: false, syncing: true, error: null });
    synchronizeViewerProgress(user)
      .then(() => { if (active) setState({ ready: true, syncing: false, error: null }); })
      .catch((error) => { if (active) setState({ ready: true, syncing: false, error }); });
    return () => { active = false; };
  }, [sessionReady, user?.id]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <ProgressSyncContext.Provider value={value}>{children}</ProgressSyncContext.Provider>;
}

export function useProgressSync() {
  return useContext(ProgressSyncContext);
}
